import type { ShadowConfig } from '../../types';
import ToggleSwitch from './ToggleSwitch';

interface ShadowEditorProps {
  shadow: ShadowConfig;
  onChange: (shadow: ShadowConfig) => void;
  label?: string;
}

export default function ShadowEditor({
  shadow,
  onChange,
  label = 'Drop Shadow',
}: ShadowEditorProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
          {label}
        </span>
        <ToggleSwitch
          checked={shadow.enabled}
          onChange={(checked) => onChange({ ...shadow, enabled: checked })}
        />
      </div>

      {shadow.enabled && (
        <div className="space-y-2 pl-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 w-10">Color</span>
            <input
              type="color"
              value={shadow.color.slice(0, 7)}
              onChange={(e) => onChange({ ...shadow, color: e.target.value + '66' })}
              className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
            />
            <input
              type="text"
              value={shadow.color}
              onChange={(e) => onChange({ ...shadow, color: e.target.value })}
              className="flex-1 px-1.5 py-0.5 text-[10px] font-mono bg-[#0a0a0a] border border-white/[0.06] rounded text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <SliderRow
            label="X"
            value={shadow.offsetX}
            min={-50}
            max={50}
            onChange={(v) => onChange({ ...shadow, offsetX: v })}
            suffix="px"
          />
          <SliderRow
            label="Y"
            value={shadow.offsetY}
            min={-50}
            max={50}
            onChange={(v) => onChange({ ...shadow, offsetY: v })}
            suffix="px"
          />
          <SliderRow
            label="Blur"
            value={shadow.blur}
            min={0}
            max={100}
            onChange={(v) => onChange({ ...shadow, blur: v })}
            suffix="px"
          />
        </div>
      )}
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  onChange,
  suffix,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  suffix: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-zinc-500 w-10">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="flex-1 h-1 bg-white/[0.06] rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500"
      />
      <span className="text-[10px] text-white/50 font-mono w-10 text-right">
        {value}
        {suffix}
      </span>
    </div>
  );
}
